// API service for remediation actions
/**
 * Start a remediation action via POST that streams SSE
 * This makes a POST call to establish the connection, then streams via SSE
 * @param {string} actionId - The action ID to remediate
 * @param {number} alertId - The specific alert ID (integer) to remediate
 * @param {Object} callbacks - Callback functions for SSE events
 * @returns {Promise<EventSource>} - Promise that resolves to EventSource instance
 */
export const startRemediateAction = async (actionId, alertId, callbacks) => {
  const {
    onStep,
    onComplete,
    onError,
    onConnectionChange
  } = callbacks;

  try {
    // Create payload with both action_id and alert_id (integer)
    const payload = { 
      action_id: actionId,
      alert_id: parseInt(alertId)
    };
    
    // Make the POST request to start the remediation
    const response = await fetch(`/api/remediate_action`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }


    // Check if we got a response with steps data
    let responseData = null;
    try {
      const responseText = await response.text();
      if (responseText) {
        responseData = JSON.parse(responseText);
        console.log('📦 POST response data:', responseData);
      }
    } catch (parseError) {
    }

    // If we got complete data in the POST response, handle it directly
    if (responseData && Array.isArray(responseData)) {
      
      // Process each step and check for completion signal
      let completionDetected = false;
      responseData.forEach((step, index) => {
        onStep?.({ step });
        
        // Check if this step contains "All steps completed."
        if (typeof step === 'string' && step.includes('All steps completed.')) {
          completionDetected = true;
        }
      });
      
      // Mark as complete, including the steps and completion status
      setTimeout(() => {
        onComplete?.({ 
          status: 'complete', 
          steps: responseData,
          allStepsCompleted: completionDetected
        });
      }, 100);
      
      // Return a mock EventSource for compatibility
      return {
        close: () => console.log('Mock EventSource closed'),
        readyState: 2 // CLOSED
      };
    }

    // If no data in POST response, fall back to SSE
    console.log('No complete data in POST response, setting up SSE connection...');

    // After successful POST, establish SSE connection
    // The backend will stream data back via SSE on the same endpoint
    const sseUrl = `/api/remediate_action?action_id=${actionId}`;
    
    const eventSource = new EventSource(sseUrl);

    eventSource.onopen = () => {
      onConnectionChange?.(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.step) {
          onStep?.(data);
        } else if (data.status === 'complete' || data.complete) {
          // Handle completion
          onComplete?.(data);
          eventSource.close();
        } else {
          onStep?.(data); // Treat unknown messages as steps
        }
      } catch (error) {
        console.error(' Error parsing remediation SSE data:', error);
        onError?.('Failed to parse remediation data');
      }
    };

    eventSource.onerror = (error) => {
      console.error(' Remediation SSE error:', error);
      onConnectionChange?.(false);
      onError?.('Remediation connection error');
    };

    return eventSource;

  } catch (error) {
    console.error(' Failed to start remediate action:', error);
    onError?.(`Failed to start remediation: ${error.message}`);
    throw error;
  }
};
